import { getBoardViewDetailsKeys } from 'containers/Ticketing/_api/queryKeys';
import type {
  TicketBoardViewColumn,
  TicketFieldFilter,
} from 'containers/Ticketing/_api/useGetBoardViewDetails/types';
import type { TicketLayoutField } from 'containers/Ticketing/_api/useGetTicketLayoutDetails/types';
import type { Sort } from 'containers/Ticketing/_api/useGetTicketsPagination/types';
import type { ImmerStateCreator } from 'hooks/useStore';
import { globalQueryClient } from 'utils/globalQueryClient';
import { getAllRecords } from 'utils/localStorageManager';
interface CreateViewParams {
  entityType: string;
  viewType: string;
}

export interface ReportViewSlice {
  allFields: TicketLayoutField[];
  ticketSearch: string;
  ticketCurrentPage: number;
  selectedViewName: string | null;
  isSelectedViewEditable: boolean;
  deleteViewId: string | null;
  deleteViewName: string | null;
  duplicateViewId: string | null;
  duplicateViewName: string | null;
  isShowCreateModal: boolean;
  isShowDeleteModal: boolean;
  isShowDuplicateModal: boolean;
  isShowFilterModal: boolean;
  isShowFieldsModal: boolean;
  filters: TicketFieldFilter[];
  fields: TicketBoardViewColumn[];
  satisfyAll: boolean;
  fieldChangeCount: number;
  filterChangeCount: number;
  setAllFields: (fields: TicketLayoutField[]) => void;
  ticketSorts: undefined | Sort[];
  isSortsChanged: boolean;
  setTicketSearch: (keyword: string) => void;
  setTicketCurrentPage: (page: number) => void;
  setFieldChangeCount: (count: number) => void;
  setFilterChangeCount: (count: number) => void;
  setSelectedViewName: (name: string) => void;
  showCreateModal: (data: CreateViewParams) => void;
  hideCreateModal: () => void;
  showDeleteModal: (id: string, name: string) => void;
  hideDeleteModal: () => void;
  showDuplicateModal: (id: string, name: string) => void;
  hideDuplicateModal: () => void;
  showFilterModal: (viewId: string) => void;
  hideFilterModal: () => void;
  showFieldsModal: (viewId: string) => void;
  hideFieldsModal: () => void;
  setFilters: (filters: TicketFieldFilter[]) => void;
  setFields: (fields: TicketBoardViewColumn[]) => void;
  setSatisfyAll: (rules: boolean) => void;
  reset: () => void;
  resetTicketPagination: () => void;
  isEditting: boolean;
  setIsEditting: (isEditFilters: boolean) => void;
  hasUnsavedChanges: () => boolean;
  setTicketSorts: (sorts: Sort[]) => void;
  setIsSortsChanged: (isChanged: boolean) => void;
  resetTicketPageAndSort: () => void;
}

export const useReportViewSlice: ImmerStateCreator<ReportViewSlice> = (
  set,
  get
) => ({
  allFields: [],
  ticketSearch: '',
  ticketCurrentPage: 0,
  isEditting: false,
  selectedViewName: null,
  isSelectedViewEditable: true,
  deleteViewId: null,
  deleteViewName: null,
  duplicateViewId: null,
  duplicateViewName: null,
  isShowCreateModal: false,
  isShowDeleteModal: false,
  isShowDuplicateModal: false,
  isShowFilterModal: false,
  isShowFieldsModal: false,
  filters: [],
  fields: [],
  fieldChangeCount: 0,
  filterChangeCount: 0,
  satisfyAll: false,
  setAllFields: (fields: TicketLayoutField[]) => {
    set(
      (state) => {
        state.reportViewSlice.allFields = fields;
      },
      false,
      'setAllFields'
    );
  },
  ticketSorts: [],
  isSortsChanged: false,
  hasUnsavedChanges: () =>
    get().reportViewSlice.filterChangeCount > 0 ||
    get().reportViewSlice.fieldChangeCount > 0 ||
    get().reportViewSlice.isSortsChanged,
  setFilterChangeCount: (count: number) => {
    set(
      (state) => {
        state.reportViewSlice.filterChangeCount = count;
      },
      false,
      'setFilterChangeCount'
    );
  },
  setFieldChangeCount: (count: number) => {
    set(
      (state) => {
        state.reportViewSlice.fieldChangeCount = count;
      },
      false,
      'setFieldChangeCount'
    );
  },
  setSatisfyAll: (rules: boolean) => {
    set(
      (state) => {
        state.reportViewSlice.satisfyAll = rules;
      },
      false,
      'setSatisfyAll'
    );
  },
  setIsEditting: (isEditFilters: boolean) =>
    set(
      (state) => {
        state.reportViewSlice.isEditting = isEditFilters;
      },
      false,
      'setIsEditFilters'
    ),
  setSelectedViewName: (name: string) =>
    set(
      (state) => {
        state.reportViewSlice.selectedViewName = name;
      },
      false,
      'setSelectedViewName'
    ),
  showCreateModal: (data: CreateViewParams) =>
    set(
      (state) => {
        state.reportViewSlice.isShowCreateModal = true;
      },
      false,
      'showCreateModal'
    ),
  hideCreateModal: () =>
    set(
      (state) => {
        state.reportViewSlice.isShowCreateModal = false;
      },
      false,
      'hideCreateModal'
    ),
  showDeleteModal: (id: string, name: string) =>
    set(
      (state) => {
        state.reportViewSlice.isShowDeleteModal = true;
        state.reportViewSlice.deleteViewId = id;
        state.reportViewSlice.deleteViewName = name;
      },
      false,
      'showDeleteModal'
    ),
  hideDeleteModal: () =>
    set(
      (state) => {
        state.reportViewSlice.isShowDeleteModal = false;
        state.reportViewSlice.deleteViewId = null;
      },
      false,
      'hideDeleteModal'
    ),
  showDuplicateModal: (id: string, name: string) =>
    set(
      (state) => {
        state.reportViewSlice.isShowDuplicateModal = true;
        state.reportViewSlice.duplicateViewId = id;
        state.reportViewSlice.duplicateViewName = name;
      },
      false,
      'showDuplicateModal'
    ),
  hideDuplicateModal: () =>
    set(
      (state) => {
        state.reportViewSlice.isShowDuplicateModal = false;
        state.reportViewSlice.duplicateViewId = null;
        state.reportViewSlice.duplicateViewName = null;
      },
      false,
      'hideDuplicateModal'
    ),
  showFilterModal: (viewId: string) =>
    set(
      (state) => {
        state.reportViewSlice.isShowFilterModal = true;
      },
      false,
      'showFilterModal'
    ),
  hideFilterModal: () =>
    set(
      (state) => {
        state.reportViewSlice.isShowFilterModal = false;
      },
      false,
      'hideFilterModal'
    ),
  showFieldsModal: (viewId: string) =>
    set(
      (state) => {
        state.reportViewSlice.isShowFieldsModal = true;
      },
      false,
      'showFieldsModal'
    ),
  hideFieldsModal: () =>
    set(
      (state) => {
        state.reportViewSlice.isShowFieldsModal = false;
      },
      false,
      'hideFieldsModal'
    ),
  setFilters: (filter: TicketFieldFilter[]) =>
    set(
      (state) => {
        state.reportViewSlice.filters = filter;
      },
      false,
      'setFilters'
    ),
  setFields: (fields: TicketBoardViewColumn[]) =>
    set(
      (state) => {
        state.reportViewSlice.fields = fields;
      },
      false,
      'setFields'
    ),

  // Shared pagination state for board view
  setTicketSearch: (keyword: string) => {
    set(
      (state) => {
        state.reportViewSlice.ticketSearch = keyword;
      },
      false,
      'setTicketSearch'
    );
  },
  setTicketCurrentPage: (page: number) => {
    set(
      (state) => {
        state.reportViewSlice.ticketCurrentPage = page;
      },
      false,
      'setTicketCurrentPage'
    );
  },
  setTicketSorts: (sorts: Sort[]) => {
    set(
      (state) => {
        state.reportViewSlice.ticketSorts = sorts;
      },
      false,
      'setTicketSorts'
    );
  },
  setIsSortsChanged: (isChanged: boolean) => {
    set(
      (state) => {
        state.reportViewSlice.isSortsChanged = isChanged;
      },
      false,
      'setIsSortsChanged'
    );
  },

  reset: () =>
    set(
      (state) => {
        const viewId = getAllRecords('ticketing-selected-view');
        const boardId = getAllRecords('ticketing-selected-board');
        globalQueryClient.refetchQueries(
          getBoardViewDetailsKeys(boardId, viewId)
        );
        state.reportViewSlice.fieldChangeCount = 0;
        state.reportViewSlice.filterChangeCount = 0;
        state.reportViewSlice.isEditting = false;
        state.reportViewSlice.fields = [];
        state.reportViewSlice.filters = [];
        state.reportViewSlice.isSortsChanged = false;
      },
      false,
      'reset'
    ),
  resetTicketPagination: () =>
    set(
      (state) => {
        state.reportViewSlice.ticketSorts = [];
        state.reportViewSlice.ticketSearch = '';
        state.reportViewSlice.ticketCurrentPage = 0;
      },
      false,
      'resetTicketPagination'
    ),
  resetTicketPageAndSort: () =>
    set(
      (state) => {
        state.reportViewSlice.ticketSorts = [];
        state.reportViewSlice.ticketCurrentPage = 0;
      },
      false,
      'resetTicketPageAndSort'
    ),
});
